import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { createOrder, getSettings, whatsappUrl, buildOrderWhatsAppText } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import LoginDialog from "@/components/LoginDialog";
import { CheckCircle2, Loader2, Wallet, ShoppingBag, MessageCircle, FileText, CreditCard, Copy } from "lucide-react";
import UpiQRCode from "@/components/UpiQRCode";
import { toast } from "sonner";

const CheckoutPage = () => {
  const { t, lang, farmer, cart, cartSubtotal, deliveryFee, cartTotal, clearCart } = useApp();
  const navigate = useNavigate();
  const [loginOpen, setLoginOpen] = useState(!farmer);
  const [placedOrder, setPlacedOrder] = useState(null);
  const [shopWhatsapp, setShopWhatsapp] = useState("");
  const [upiId, setUpiId] = useState("");
  const [upiName, setUpiName] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("COD");
  const [paymentRef, setPaymentRef] = useState("");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    delivery_name: farmer?.name || "",
    delivery_mobile: farmer?.mobile || "",
    delivery_address: farmer?.address || "",
    notes: "",
  });

  useEffect(() => {
    getSettings().then((s) => {
      setShopWhatsapp(s.shop_whatsapp || "");
      setUpiId(s.upi_id || "");
      setUpiName(s.upi_name || "");
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (farmer) {
      setLoginOpen(false);
      setForm((p) => ({
        ...p,
        delivery_name: p.delivery_name || farmer.name,
        delivery_mobile: p.delivery_mobile || farmer.mobile,
        delivery_address: p.delivery_address || farmer.address,
      }));
    }
  }, [farmer]);

  useEffect(() => {
    if (cart.length === 0 && !placedOrder) {
      navigate("/products");
    }
  }, [cart.length, placedOrder, navigate]);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!farmer) {
      setLoginOpen(true);
      return;
    }
    if (!form.delivery_name || !form.delivery_mobile || !form.delivery_address) {
      toast.error("Please fill all delivery details");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        farmer_id: farmer.id,
        items: cart.map((it) => ({
          product_id: it.product_id,
          name_en: it.name_en,
          name_hi: it.name_hi,
          price: it.price,
          quantity: it.quantity,
          unit: it.unit,
          image_path: it.image_path,
        })),
        delivery_address: form.delivery_address,
        delivery_mobile: form.delivery_mobile,
        delivery_name: form.delivery_name,
        notes: form.notes,
        payment_method: paymentMethod,
        payment_ref: paymentRef,
      };
      const order = await createOrder(payload);
      setPlacedOrder(order);
      clearCart();
      toast.success(t("orderPlaced"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Order failed");
    } finally {
      setLoading(false);
    }
  };

  if (placedOrder) {
    const waHref = shopWhatsapp
      ? whatsappUrl(shopWhatsapp, buildOrderWhatsAppText(placedOrder, lang === "hi"))
      : null;
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center" data-testid="order-success">
        <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-10 h-10 text-emerald-600" strokeWidth={2} />
        </div>
        <h1 className={`font-heading font-bold text-3xl text-[#1E293B] ${lang === "hi" ? "font-devanagari" : ""}`}>
          {t("orderPlaced")}
        </h1>
        <p className="mt-2 text-slate-600">{t("orderPlacedDesc")}</p>
        <div className="mt-6 p-5 rounded-xl border border-[#E2E8F0] bg-white text-left">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-slate-600">{t("orderNumber")}</span>
            <span className="font-mono font-semibold" data-testid="order-number">{placedOrder.order_number}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">{t("total")}</span>
            <span className="font-heading font-bold text-[#2D5A27]">₹{placedOrder.total.toFixed(0)}</span>
          </div>
        </div>

        {waHref && (
          <div className="mt-5 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-left" data-testid="wa-notify-block">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center shrink-0">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-heading font-semibold text-emerald-900 text-sm">{t("notifyShop")}</p>
                <p className="text-xs text-emerald-800 mt-1">{t("notifyShopDesc")}</p>
                <a
                  href={waHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="wa-notify-btn"
                  className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold pressable"
                >
                  <MessageCircle className="w-4 h-4" /> {t("sendWhatsApp")}
                </a>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={() => navigate("/orders")} data-testid="view-orders-btn" className="rounded-full bg-[#2D5A27] hover:bg-[#21421D] text-white h-12 px-6">
            {t("myOrders")}
          </Button>
          <Button onClick={() => navigate(`/invoice/${placedOrder.id}`)} variant="outline" data-testid="view-invoice-btn" className="rounded-full h-12 px-6 border-[#2D5A27] text-[#2D5A27]">
            <FileText className="w-4 h-4 mr-1.5" /> {t("downloadInvoice")}
          </Button>
          <Button onClick={() => navigate("/products")} variant="outline" className="rounded-full h-12 px-6 border-[#E2E8F0]">
            {t("continueShopping")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="checkout-page">
      <h1 className={`font-heading font-bold text-3xl text-[#1E293B] mb-6 ${lang === "hi" ? "font-devanagari" : ""}`}>
        {t("checkout")}
      </h1>

      {!farmer && !loginOpen && (
        <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-between">
          <p className="text-sm text-amber-900">{t("loginSubtitle")}</p>
          <Button onClick={() => setLoginOpen(true)} size="sm" data-testid="checkout-login-btn" className="rounded-full bg-[#2D5A27] text-white">
            {t("login")}
          </Button>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-8">
        <form onSubmit={submit} className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
            <h2 className="font-heading font-semibold text-lg text-[#1E293B] mb-4">{t("deliveryDetails")}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="d-name" className="text-sm font-medium">{t("fullName")}</Label>
                <Input id="d-name" data-testid="checkout-name" value={form.delivery_name} onChange={update("delivery_name")} className="mt-1.5 h-12 bg-white" required />
              </div>
              <div>
                <Label htmlFor="d-mobile" className="text-sm font-medium">{t("mobileNumber")}</Label>
                <Input id="d-mobile" data-testid="checkout-mobile" value={form.delivery_mobile} onChange={update("delivery_mobile")} maxLength={10} className="mt-1.5 h-12 bg-white" required />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="d-addr" className="text-sm font-medium">{t("address")}</Label>
                <Textarea id="d-addr" data-testid="checkout-address" value={form.delivery_address} onChange={update("delivery_address")} rows={3} className="mt-1.5 bg-white" required />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="d-notes" className="text-sm font-medium">{t("orderNotes")}</Label>
                <Textarea id="d-notes" data-testid="checkout-notes" value={form.notes} onChange={update("notes")} rows={2} className="mt-1.5 bg-white" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
            <h2 className="font-heading font-semibold text-lg text-[#1E293B] mb-4">
              {lang === "hi" ? "भुगतान का तरीका" : "Payment Method"}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setPaymentMethod("COD")}
                data-testid="pay-method-cod"
                className={`text-left p-4 rounded-xl border-2 transition-all ${
                  paymentMethod === "COD"
                    ? "border-[#2D5A27] bg-emerald-50"
                    : "border-[#E2E8F0] hover:border-slate-300"
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Wallet className={`w-5 h-5 ${paymentMethod === "COD" ? "text-[#2D5A27]" : "text-slate-400"}`} />
                  <p className="font-heading font-semibold text-[#1E293B]">{t("codAvailable")}</p>
                </div>
                <p className="text-xs text-slate-500">
                  {lang === "hi" ? "डिलीवरी पर भुगतान करें" : "Pay on delivery"}
                </p>
              </button>

              <button
                type="button"
                onClick={() => setPaymentMethod("UPI")}
                disabled={!upiId}
                data-testid="pay-method-upi"
                className={`text-left p-4 rounded-xl border-2 transition-all ${
                  !upiId ? "opacity-40 cursor-not-allowed border-[#E2E8F0]" :
                  paymentMethod === "UPI"
                    ? "border-[#D97706] bg-amber-50"
                    : "border-[#E2E8F0] hover:border-slate-300"
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <CreditCard className={`w-5 h-5 ${paymentMethod === "UPI" ? "text-[#D97706]" : "text-slate-400"}`} />
                  <p className="font-heading font-semibold text-[#1E293B]">UPI</p>
                </div>
                <p className="text-xs text-slate-500">
                  {lang === "hi" ? "अभी भुगतान करें (GPay/PhonePe/Paytm)" : "Pay now (GPay/PhonePe/Paytm)"}
                </p>
              </button>
            </div>

            {paymentMethod === "UPI" && upiId && (
              <div className="mt-5 p-4 rounded-xl bg-amber-50 border border-amber-200" data-testid="upi-payment-block">
                <div className="grid md:grid-cols-2 gap-4 items-center">
                  <div className="flex justify-center md:justify-start">
                    <UpiQRCode upiId={upiId} name={upiName} amount={cartTotal} note={`Order to ${form.delivery_name || "Krishidut"}`} size={180} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#1E293B]">
                      {lang === "hi" ? "किसी भी UPI app से scan करें" : "Scan with any UPI app"}
                    </p>
                    <p className="text-xs text-slate-600 mt-1">
                      {lang === "hi"
                        ? "GPay, PhonePe, Paytm, BHIM — कोई भी UPI app से QR scan करके भुगतान करें।"
                        : "GPay, PhonePe, Paytm, BHIM — any UPI app works."}
                    </p>
                    <div className="mt-3">
                      <Label htmlFor="upi-ref" className="text-xs font-medium">
                        {lang === "hi" ? "UPI Transaction ID (payment के बाद)" : "UPI Transaction ID (after payment)"}
                      </Label>
                      <Input
                        id="upi-ref"
                        value={paymentRef}
                        onChange={(e) => setPaymentRef(e.target.value)}
                        data-testid="upi-txn-ref"
                        placeholder={lang === "hi" ? "उदा. 123456789012" : "e.g. 123456789012"}
                        className="mt-1 h-10 bg-white font-mono text-sm"
                      />
                    </div>
                    <a
                      href={`upi://pay?pa=${encodeURIComponent(upiId)}&pn=${encodeURIComponent(upiName)}&cu=INR&am=${cartTotal.toFixed(2)}`}
                      data-testid="upi-open-app-btn"
                      className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#D97706] hover:bg-[#B45309] text-white text-sm font-semibold"
                    >
                      <CreditCard className="w-4 h-4" /> {lang === "hi" ? "UPI app खोलें" : "Open UPI app"}
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>

          <Button
            type="submit"
            disabled={loading || !farmer}
            data-testid="place-order-btn"
            className="w-full h-14 rounded-full bg-[#2D5A27] hover:bg-[#21421D] text-white font-semibold text-base pressable"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
              <>
                <ShoppingBag className="w-5 h-5 mr-2" />
                {t("placeOrder")} — ₹{cartTotal.toFixed(0)}
              </>
            )}
          </Button>
        </form>

        <div className="lg:col-span-1">
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 sticky top-24" data-testid="checkout-summary">
            <h3 className="font-heading font-semibold text-lg mb-4">{t("orderSummary")}</h3>
            <div className="space-y-3 max-h-64 overflow-y-auto pr-1 mb-4">
              {cart.map((it) => {
                const name = lang === "hi" ? it.name_hi : it.name_en;
                return (
                  <div key={it.product_id} className="flex justify-between text-sm">
                    <div className="flex-1 min-w-0">
                      <p className="truncate font-medium text-[#1E293B]">{name}</p>
                      <p className="text-xs text-slate-500">{it.quantity} × ₹{it.price.toFixed(0)}</p>
                    </div>
                    <span className="font-semibold">₹{(it.price * it.quantity).toFixed(0)}</span>
                  </div>
                );
              })}
            </div>
            <div className="border-t border-[#E2E8F0] pt-3 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-slate-600">{t("subtotal")}</span><span>₹{cartSubtotal.toFixed(0)}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">{t("deliveryFee")}</span><span className={deliveryFee === 0 ? "text-emerald-600" : ""}>{deliveryFee === 0 ? "FREE" : `₹${deliveryFee.toFixed(0)}`}</span></div>
              <div className="flex justify-between text-base pt-2 border-t border-[#E2E8F0]">
                <span className="font-heading font-semibold">{t("total")}</span>
                <span className="font-heading font-bold text-[#2D5A27]">₹{cartTotal.toFixed(0)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <LoginDialog open={loginOpen} onOpenChange={setLoginOpen} />
    </div>
  );
};

export default CheckoutPage;
